import { useCallback, useEffect, useState } from "react";
import useAccount from "@/hooks/useAccount";
import {
  getAirdropClaimInfo,
  getValidatorAirdropClaimInfo,
} from "@/services/network";
import { writeContract, waitForTransaction, switchNetwork } from "@wagmi/core";
import { toast } from "react-toastify";
import { useNetwork, useContractReads } from "wagmi";
import {
  tokenAirdropContractInfo,
  validatorAirdropContractInfo,
} from "@/services/contract";
import { isUserRejectError, isSkipError } from "@/utils/validate";
import { parseUnits } from "viem";
import { captureException } from "@sentry/nextjs";

export default function useUserAirdrop() {
  const { address } = useAccount();
  const [basicClaimLoading, setBasicClaimLoading] = useState(false);
  const [validatorClaimLoading, setValidatorClaimLoading] = useState(false);
  const { chain } = useNetwork();
  const [basicClaimed, setBasicClaimed] = useState(false);
  const [validatorClaimed, setValidatorClaimed] = useState(false);

  const { data: validatorContractData, error } = useContractReads({
    watch: false,
    scopeKey: "validator-airdrop-contract",
    contracts: address
      ? [
          // {
          //   ...validatorAirdropContractInfo,
          //   functionName: "claimed",
          //   args: [address],
          // },
          {
            ...tokenAirdropContractInfo,
            functionName: "claimed",
            args: [address],
          },
        ]
      : [],
  } as any);

  console.log(
    "validatorContractData",
    validatorContractData,
    tokenAirdropContractInfo,
    validatorAirdropContractInfo,
    error
  );

  useEffect(() => {
    if (validatorContractData) {
      setBasicClaimed(!!validatorContractData?.[0]?.result);
      // setValidatorClaimed(!!validatorContractData?.[0]?.result);
    }
  }, [validatorContractData]);

  const validatorClaim = useCallback(async () => {
    try {
      setValidatorClaimLoading(true);
      if (chain?.id !== validatorAirdropContractInfo.chainId) {
        await switchNetwork({
          chainId: validatorAirdropContractInfo.chainId,
        });
      }
      const claimInfo = await getValidatorAirdropClaimInfo({
        address: address as any,
      });
      const trans = await writeContract({
        ...validatorAirdropContractInfo,
        functionName: "claim",
        args: [claimInfo.tokenIds, claimInfo.amounts, claimInfo.merkleProofs],
      });
      console.log("trans", trans);
      try {
        const transReceipt = await waitForTransaction({
          hash: trans.hash,
        });
        if (transReceipt.status === "reverted") {
          throw new Error("Claim fail");
        }
      } catch (e: any) {
        if (!(await isSkipError(e))) {
          throw e;
        }
      }

      setValidatorClaimed(true);
      toast.success("Claim success");
    } catch (e: any) {
      if (isUserRejectError(e)) {
        return;
      }
      console.error(e);
      toast.error(e.message);
      throw new Error(e.message);
    } finally {
      setValidatorClaimLoading(false);
    }
  }, [address]);

  const basicClaim = useCallback(async () => {
    let claimInfo;
    try {
      setBasicClaimLoading(true);
      if (chain?.id !== tokenAirdropContractInfo.chainId) {
        await switchNetwork({
          chainId: tokenAirdropContractInfo.chainId,
        });
      }
      claimInfo = await getAirdropClaimInfo({
        address: address as any,
      });
      const trans = await writeContract({
        ...tokenAirdropContractInfo,
        functionName: "claim",
        args: [
          address,
          parseUnits(String(claimInfo.amount), 18),
          claimInfo.proof,
        ],
      });
      console.log("trans", trans.hash);

      try {
        const transReceipt = await waitForTransaction({
          hash: trans.hash,
        });
        if (transReceipt.status === "reverted") {
          throw new Error("Claim fail");
        }
      } catch (e: any) {
        if (!(await isSkipError(e))) {
          throw e;
        }
      }
      setBasicClaimed(true);
      toast.success("Claim success");
    } catch (e: any) {
      if (isUserRejectError(e)) {
        return;
      }
      console.log("claimInfo", JSON.stringify(claimInfo));
      console.error(e);
      captureException(e);
      if (await isSkipError(e)) {
        return;
      }
      toast.error(e.message);
    } finally {
      setBasicClaimLoading(false);
    }
  }, [address]);

  return {
    basicClaim,
    basicClaimLoading,
    basicClaimed,
    validatorClaim,
    validatorClaimLoading,
    validatorClaimed: false,
  };
}
